import { Badge } from '@/components/atoms';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';

import styles from './TenantOperationPrimitives.module.css';

export type CostMeasurementStatus = 'measured' | 'estimated' | 'not_instrumented';

interface CostReadoutProps {
  status: CostMeasurementStatus;
  amountUsd?: number | null;
  detail?: string | null;
}

export function CostReadout({ status, amountUsd, detail }: CostReadoutProps) {
  const { t } = useTranslation([
    'common',
    'nav',
    'board',
    'articles',
    'analytics',
    'feeddrive',
    'config',
    'strategist',
  ]);
  void t;
  const value =
    status === 'not_instrumented' || amountUsd == null
      ? 'Custo ainda não medido'
      : `US$ ${amountUsd.toFixed(6)}`;
  const label =
    status === 'estimated' ? 'Estimativa' : status === 'measured' ? 'Custo real' : 'Medição';

  return (
    <div className={styles.cost} aria-label={`${label}: ${value}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      {detail && <span>{detail}</span>}
    </div>
  );
}

export type TenantRunState =
  | 'received'
  | 'matched'
  | 'suppressed'
  | 'held'
  | 'failed'
  | 'cancelled';

const RUN_LABELS: Record<TenantRunState, string> = {
  received: 'Recebida',
  matched: 'Correspondeu',
  suppressed: 'Suprimida',
  held: 'Em espera',
  failed: 'Falhou',
  cancelled: 'Cancelada',
};

function runTone(state: TenantRunState): 'neutral' | 'info' | 'ok' | 'warn' | 'crit' {
  if (state === 'failed') return 'crit';
  if (state === 'held' || state === 'suppressed') return 'warn';
  if (state === 'matched') return 'info';
  return 'neutral';
}

interface RunStateCardProps {
  state: TenantRunState;
  title: string;
  idempotencyKey: string;
  reasonCodes?: string[];
  errorMessage?: string | null;
  traceId?: string | null;
  children?: ReactNode;
}

export function RunStateCard({
  state,
  title,
  idempotencyKey,
  reasonCodes = [],
  errorMessage,
  traceId,
  children,
}: RunStateCardProps) {
  return (
    <article className={styles.run} aria-label={title}>
      <header className={styles.runHead}>
        <strong>{title}</strong>
        <Badge tone={runTone(state)}>{RUN_LABELS[state]}</Badge>
      </header>
      <div className={styles.runMeta}>
        <span>
          chave idempotente <code>{idempotencyKey}</code>
        </span>
        {traceId && (
          <span>
            trace <code>{traceId}</code>
          </span>
        )}
      </div>
      {reasonCodes.length > 0 && (
        <ul className={styles.reasons} aria-label="Motivos da decisão">
          {reasonCodes.map((reason) => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      )}
      {errorMessage && (
        <p className={styles.error} role="alert">
          {errorMessage}
        </p>
      )}
      {children}
    </article>
  );
}
